import React, { useEffect, useMemo, useCallback, useState } from "react";
import styles from "./BuildQuote.module.scss";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../../enums/ButtonAttributesEnums";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import { BuildQuotesStates } from "../../../../enums/BuildQuoteStates";
import Quote from "./Quote";
import BuildEstimate from "./Estimate";
import { Modal, Col, Row, Select, message, notification } from "antd";
import InputField from "../../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import CreateJobServices from "../../../../services/createJob.services";
import { useFormContext } from "react-hook-form";
import {
  getDropdownOptions,
  getSendContactDropdownOptions,
} from "../../../../utils/getDropdownOptions.tsx";
import { isValidName } from "../../../../Utilities/regex";
import SuccessModal from "../../../../reusable-components/SuccessModal";
import {
  SUCCESS_CONTENT,
  SUCCESS_CONTENT_2,
  SUCCESS_CONTENT_3,
  ErrorTypes,
  BuildQuoteFormFields,
  notificationConstants,
  estimateValidatingFields,
  validatingFields,
  estimateNotificationMessages,
  ValidationMessages,
} from "./constants";
import TNLoader from "../../../../reusable-components/TNLoader";
import Label from "../../../../reusable-components/Label";
import ErrorMessage from "../../../../reusable-components/ErrorMessage";
import { regexPatterns } from "../../../../utils/regexUtil";
import { fieldNames, getDocumentRows, hasFormChanged } from "./constant";
import DocumentServices from "../../../../services/documents.services";
import { documentType } from "../../../Documents/constants";
import { ApiResponse } from "../../../../enums/ApiResponse";
import { jobStatus } from "../constants";
import { fetchFormattedDate } from "../../../../utils/dateUtils";
import moment from "moment";
import { useBuildQuoteState } from "../../../../../src/hooks/BuildQuoteHooks/useBuildQuoteState";
import { useSignatureState } from "../../../../../src/hooks/BuildQuoteHooks/useSignatureState";
import { useModalState } from "../../../../../src/hooks/BuildQuoteHooks/useModalState";
import { useQuoteValidation } from "../../../../../src/utils/BuildQuoteUtils/quoteValidation";
import { usePayloadBuilder } from "../../../../../src/utils/BuildQuoteUtils/payloadBuilders";
import { downloadUtil } from "../../../../utils/downloadUtil";
import { quoteHeader } from "../../../Documents/QuoteEdit/constants";

const { QUOTE_DATE_FIELD } = BuildQuoteFormFields;

const BuildQuote = ({
  leadId,
  jobNumber,
  handleQuoteSharedStatus,
  isEditable,
  jobType,
}) => {
  const { getValues, watch, setValue, setError, clearErrors } =
    useFormContext();
  const { IS_ESTIMATE, IS_QUOTE, IS_READ_ONLY } = BuildQuotesStates;
  const { emailTestRegex } = regexPatterns;

  // Custom hooks for state management
  const {
    isEstimateEdit,
    isQuoteEdit,
    showEstimate,
    estimateTickedRows,
    quoteTickedRows,
    setIsEstimateEdit,
    setIsQuoteEdit,
    setEstimateTickedRows,
    setQuoteTickedRows,
    handleToggleShowEstimate,
    resetEditStates,
  } = useBuildQuoteState();

  const { signatureState, updateSignature } = useSignatureState();
  const {
    isSuccessModalOpen,
    isSendCustomerOpen,
    isAddCustomerOpen,
    isSuccessModal,
    isStatusError,
    setIsSuccessModalOpen,
    setIsSendCustomerOpen,
    setIsAddCustomerOpen,
    setSuccessModal,
    setStatusError,
    closeAllModals,
  } = useModalState();

  const { validateDownloadAndSend, validateQuoteEditFields } =
    useQuoteValidation();
  const { buildDownloadPayload } = usePayloadBuilder();

  const {
    DonwloadEstimate,
    AddContact,
    FetchAllDocuments,
    SaveTruckAndMaterialsDetails,
    FetchCustomerAccounts,
    FetchContacts,
    FetchQuote,
    FetchSalesPerson 
  } = CreateJobServices();

  const { data: salesPersonList } = FetchSalesPerson();

  const salesPersonId = getValues("salesPerson");
  const salesPerson = useMemo(
    () => salesPersonList?.find((item) => item.id === salesPersonId),
    [salesPersonList, salesPersonId]
  );


  const { mutateAsync: ChangeJobStatus } = SaveTruckAndMaterialsDetails();
  const { FetchDocumentDetails } = DocumentServices();

  const { data: templateDocData, refetch: refetchTemplateDocData } =
    FetchDocumentDetails({ isCrm: true });
  const { mutateAsync: downloadEstimate, isPending: downloadLoading } =
    DonwloadEstimate();
  const { mutateAsync: addContact } = AddContact();
  const { data: documentsData, refetch: refecthDocuments } =
    FetchAllDocuments(leadId);
  const { data } = FetchCustomerAccounts();
  const {
    data: contactDetails,
    refetch: refetchContacts,
    isLoading: isContactLoading,
  } = FetchContacts(getValues("customerAccount"));
  const { data: QuoteDetails } = FetchQuote(leadId);

  const customerAccount = getValues("customerAccount");
  const customerDropdownOptions = useMemo(
    () => getDropdownOptions(data),
    [data]
  );
  const contactDropdownOptions = useMemo(
    () => getSendContactDropdownOptions(contactDetails),
    [contactDetails]
  );

  const company = useMemo(
    () =>
      customerDropdownOptions?.find(
        (obj) => obj.value === getValues(fieldNames.CUSTOMER_ACCOUNT)
      )?.label || null,
    [customerDropdownOptions, getValues]
  );

  const quoteTemplateRows = useMemo(
    () => getDocumentRows(templateDocData, documentType.quote),
    [templateDocData]
  );

  const estimateTemplateRows = useMemo(
    () => getDocumentRows(templateDocData, documentType.estimate),
    [templateDocData]
  );

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedContact, setSelectedContact] = useState(undefined);
  const [quoteSelectedField, setQuoteSelectedField] = useState(
    QuoteDetails?.quoteSelectedField ??
      quoteTemplateRows?.find((quote) => quote?.isPrimary === true)?.value
  );
  const [estimateSelectedField, setEstimateSelectedField] = useState(
    QuoteDetails?.estimateSelectedField ??
      estimateTemplateRows?.find((estimate) => estimate?.isPrimary === true)
        ?.value
  );

  const [quoteFormContent, setQuoteFormContent] = useState(null);
  const [estimateFormContent, setEstimateFormContent] = useState(null);

  useEffect(() => {
    const existingUrl = getValues(fieldNames.SIGN_IMAGE_SRC);
    const existingText = getValues(fieldNames.SIGN_TEXT);

    if (existingUrl || existingText) {
      updateSignature(
        existingUrl,
        existingText ? "manual" : "upload",
        existingText
      );
    }
  }, [getValues, updateSignature]);

  useEffect(() => {
    if (QuoteDetails) {
      const {
        //quote fields
        companyName,
        companyAddress,
        companyContact,
        quoteAddressTerms,
        quoteHeader,
        quote_agreementDescription: mainDescription,
        quote_paymemtTerms: paymentTerms,
        quote_signature: signatureTerms,
        //estimate fields
        estimate_companyName: estimateCompanyName,
        estimate_companyAddress: estimateCompanyAddress,
        estimate_companyContact: estimateCompanyContact,
        estimateAddressTerms,
        estimateHeader,
        estimate_agreementSectionBottom: truckingTerms,
        estimate_thanksNotes: thanksNote,
        estimate_agreementSectionTop, // mainDescription for estimate
        estimate_paymentTerms // paymentTerms for estimate
      } = QuoteDetails || {};
      if (QuoteDetails?.quoteSelectedField) {
        const signatureUrl = QuoteDetails?.img_url?.split("?")?.[0];
        if (signatureUrl) {
          updateSignature(signatureUrl, "upload", null);
        }
        setValue(fieldNames.QUOTE_PRINT_NAME, QuoteDetails?.quote_print_name);
        setValue(fieldNames.QUOTE_TITLE, QuoteDetails?.quote_title);
        setValue(fieldNames.QUOTE_PHONE, QuoteDetails?.quote_phone);
        setValue(fieldNames.QUOTE_EMAIL, QuoteDetails?.quote_email);
        setValue(
          fieldNames.QUOTE_DATE,
          QuoteDetails?.quote_date?.split(" ")?.join("")
        );
        const quoteFormData = {
          companyName,
          companyAddress,
          companyContact,
          quoteAddressTerms,
          quoteHeader,
          mainDescription,
          paymentTerms,
          signatureTerms,
        };
        setQuoteFormContent(quoteFormData);
        setQuoteSelectedField(QuoteDetails?.quoteSelectedField ?? null);
      }

      if (QuoteDetails?.estimateSelectedField) {
        if(!QuoteDetails?.quote_date) {
          setValue(
            fieldNames.QUOTE_DATE,
            fetchFormattedDate()?.split(" ")?.join("")
          );
        }
        const estimateFormData = {
          estimateCompanyName,
          estimateCompanyAddress,
          estimateCompanyContact,
          estimateAddressTerms,
          estimateHeader,
          truckingTerms,
          thanksNote,
          mainDescription: estimate_agreementSectionTop,
          paymentTerms: estimate_paymentTerms,
        };
        setEstimateFormContent(estimateFormData);
        setEstimateSelectedField(QuoteDetails?.estimateSelectedField ?? null);
      }
    }

    if (!QuoteDetails) {
      updateSignature(null, "upload", null);
      setValue(
        fieldNames.QUOTE_DATE,
        fetchFormattedDate()?.split(" ")?.join("")
      );
      setValue(fieldNames.QUOTE_PRINT_NAME, null);
      setValue(fieldNames.QUOTE_TITLE, null);
      setValue(fieldNames.QUOTE_PHONE, null);
      setValue(fieldNames.QUOTE_EMAIL, null);
    }

    if (!QuoteDetails?.quoteSelectedField && quoteTemplateRows?.length) {
      setQuoteFormContent(
        quoteTemplateRows?.find((quote) => quote?.isPrimary === true)
      );
      setQuoteSelectedField(
        quoteTemplateRows?.find((quote) => quote?.isPrimary === true)?.value
      );
    }

    if (!QuoteDetails?.estimateSelectedField && estimateTemplateRows?.length) {
      setEstimateSelectedField(
        estimateTemplateRows?.find((estimate) => estimate?.isPrimary === true)
          ?.value
      );
      setEstimateFormContent(
        estimateTemplateRows?.find((estimate) => estimate?.isPrimary === true)
      );
    }
  }, [
    QuoteDetails,
    isEditable,
    updateSignature,
    estimateTemplateRows,
    quoteTemplateRows,
    setValue,
  ]);

  useEffect(() => {
    refetchContacts();
  }, [selectedCustomer, customerAccount, refetchContacts]);

  const handleSendToCustomerModalOpen = useCallback(() => {
    if (
      !validateDownloadAndSend(
        showEstimate,
        isEstimateEdit,
        isQuoteEdit,
        signatureState
      )
    ) {
      return;
    }

    if (
      (showEstimate === IS_ESTIMATE && estimateTickedRows.length === 0) ||
      (showEstimate === IS_QUOTE && quoteTickedRows.length === 0)
    ) {
      message.error(notificationConstants.SEND_SELECT_ERROR);
      return;
    }
    setIsSendCustomerOpen(true);
  }, [
    showEstimate,
    isEstimateEdit,
    isQuoteEdit,
    signatureState,
    estimateTickedRows,
    quoteTickedRows,
    validateDownloadAndSend,
  ]);

  const handleAddCustomerModalOpen = useCallback(
    () => setIsAddCustomerOpen(true),
    []
  );

  const handleToggleIsEstimateEdit = useCallback(async () => {
    if (isEstimateEdit) {
      const hasInvalidField = estimateValidatingFields?.map((field) => {
        const value = getValues(field);
        const isEmpty =
          value === null || value === undefined || value?.trim() === "";

        if (isEmpty) {
          setError(field, {
            type: ErrorTypes.HEADERS.REQUIRED,
            message: ValidationMessages.HEADERS.REQUIRED,
          });
          return false;
        }
        return true;
      });

      if (hasInvalidField?.includes(false)) return;

      const success = await saveEstimate();
      if (success) setIsEstimateEdit(false);
    } else {
      setIsEstimateEdit(true);
    }
  }, [isEstimateEdit, getValues, setError]);

  const handleToggleIsQuoteEdit = useCallback(async () => {
    if (isQuoteEdit) {
      if (!signatureState.url) {
        message.error(notificationConstants.UPLOAD_SIGNATURE_OR_NAME);
        return false;
      }

      if (!validateQuoteEditFields()) return;

      const hasInvalidField = validatingFields?.map((field) => {
        const value = getValues(field);
        const isEmpty =
          value === null || value === undefined || value?.trim() === "";

        if (isEmpty) {
          setError(field, {
            type: ErrorTypes.HEADERS.REQUIRED,
            message: ValidationMessages.HEADERS.REQUIRED,
          });
          return false;
        }
        return true;
      });

      if (hasInvalidField?.includes(false)) return;

      clearErrors();
      const success = await saveQuote();
      if (success) setIsQuoteEdit(false);
    } else {
      setIsQuoteEdit(true);
    }
  }, [
    isQuoteEdit,
    signatureState,
    getValues,
    setError,
    clearErrors,
    validateQuoteEditFields,
  ]);

  const handleDownloadDocument = useCallback(async () => {
    if (
      !validateDownloadAndSend(
        showEstimate,
        isEstimateEdit,
        isQuoteEdit,
        signatureState
      )
    )
      return;

    if (
      (showEstimate === IS_ESTIMATE && estimateTickedRows.length === 0) ||
      (showEstimate === IS_QUOTE && quoteTickedRows.length === 0)
    ) {
      message.error(notificationConstants.SELECT_ITEMS_TO_DOWNLOAD);
      return;
    }

    const payload = buildDownloadPayload(
      getValues,
      showEstimate,
      customerAccount,
      company,
      leadId,
      jobNumber,
      estimateTickedRows,
      quoteTickedRows,
      estimateSelectedField,
      quoteSelectedField,
      signatureState
    );

    const data = await downloadEstimate({...payload, isDownload: true});
    if (data?.message === "Success") {
      handleQuoteSharedStatus(true);
      await downloadUtil(data?.fileUrl, data?.fileName);
    }
  }, [
    showEstimate,
    isEstimateEdit,
    isQuoteEdit,
    signatureState,
    estimateTickedRows,
    quoteTickedRows,
    customerAccount,
    company,
    validateDownloadAndSend,
    buildDownloadPayload,
    getValues,
    leadId,
    jobNumber,
    estimateSelectedField,
    quoteSelectedField,
    downloadEstimate,
    handleQuoteSharedStatus,
    refecthDocuments,
  ]);

  const saveQuote = useCallback(async () => {
    const payload = buildDownloadPayload(
      getValues,
      showEstimate,
      customerAccount,
      company,
      leadId,
      jobNumber,
      estimateTickedRows,
      quoteTickedRows,
      estimateSelectedField,
      quoteSelectedField,
      signatureState
    );

    if (!hasFormChanged(payload, QuoteDetails)) {
      message.info(notificationConstants.NO_CHANGES_TO_SAVE);
      return true;
    }

    const data = await downloadEstimate(payload, {
      onSuccess: async (data) => {
        setQuoteFormContent({
          ...payload,
          mainDescription: payload?.quote_agreementDescription,
          paymentTerms: payload?.quote_paymemtTerms,
          signatureTerms: payload?.quote_signature,
        });
        clearErrors();
        handleQuoteSharedStatus(false);
        await ChangeJobStatus({
          jobId: leadId,
          stageId: jobStatus.buildQuote,
        });
        message.success("Quote saved successfully!");
      },
      onError: () => message.error("Unable to save quote data"),
    });

    return data?.message === "Success";
  }, [
    getValues,
    showEstimate,
    customerAccount,
    company,
    QuoteDetails,
    estimateTickedRows,
    quoteTickedRows,
    estimateSelectedField,
    quoteSelectedField,
    signatureState,
    buildDownloadPayload,
    downloadEstimate,
    clearErrors,
    handleQuoteSharedStatus,
    ChangeJobStatus,
    leadId,
  ]);

  const saveEstimate = useCallback(async () => {
    const payload = buildDownloadPayload(
      getValues,
      showEstimate,
      customerAccount,
      company,
      leadId,
      jobNumber,
      estimateTickedRows,
      quoteTickedRows,
      estimateSelectedField,
      quoteSelectedField,
      signatureState
    );

    if (!hasFormChanged(payload, QuoteDetails)) {
      message.info(notificationConstants.NO_CHANGES_TO_SAVE);
      return true;
    }

    const data = await downloadEstimate(payload, {
      onSuccess: async (data) => {
        setEstimateFormContent({
          ...payload,
          mainDescription: payload?.estimate_agreementSectionTop,
          truckingTerms: payload?.estimate_agreementSectionBottom,
          paymentTerms: payload?.estimate_paymentTerms,
          thanksNote: payload?.estimate_thanksNotes,
          estimateCompanyName: payload?.estimate_companyName,
          estimateCompanyAddress: payload?.estimate_companyAddress,
          estimateCompanyContact: payload?.estimate_companyContact,
        });
        clearErrors();
        handleQuoteSharedStatus(false);
        await ChangeJobStatus({
          jobId: leadId,
          stageId: jobStatus.buildQuote,
        });
        message.success(estimateNotificationMessages.success);
      },
      onError: () => message.error(estimateNotificationMessages.failure),
    });

    return data?.message === ApiResponse.SUCCESS;
  }, [
    getValues,
    showEstimate,
    customerAccount,
    company,
    QuoteDetails,
    estimateTickedRows,
    quoteTickedRows,
    estimateSelectedField,
    quoteSelectedField,
    signatureState,
    buildDownloadPayload,
    downloadEstimate,
    clearErrors,
    handleQuoteSharedStatus,
    ChangeJobStatus,
    leadId,
  ]);

  const handleSelectedCustomer = useCallback((selectedCustomer: string) => {
    setSelectedCustomer(selectedCustomer);
  }, []);

  const handleSelectContact = useCallback(
    (selectConatct: string) => {
      setSelectedContact(selectConatct);
      setStatusError(false);
    },
    [setStatusError]
  );

  const handleSendToCustomerModalSubmit = useCallback(async () => {
    let isValid = true;
    if (!selectedContact) {
      setStatusError(true);
      isValid = false;
    }
    if (isValid) {
      if (showEstimate !== IS_ESTIMATE && showEstimate !== IS_QUOTE) {
        message.error("Select quote or estimate");
      }
      if (
        (showEstimate === IS_ESTIMATE && isEstimateEdit) ||
        (showEstimate === IS_QUOTE && isQuoteEdit)
      ) {
        message.error("Click on edit button to save the contents");
      }
      const payload = buildDownloadPayload(
        getValues,
        showEstimate,
        customerAccount,
        company,
        leadId,
        jobNumber,
        estimateTickedRows,
        quoteTickedRows,
        estimateSelectedField,
        quoteSelectedField,
        signatureState,
        true,
        selectedContact
      );

      const data = await downloadEstimate(payload, {
        onSuccess: () => {
          handleQuoteSharedStatus(true);
          setSuccessModal(true);
        },
        onError: (error) => {
          notification.error({ message: "Unable to send the quote" });
        },
      });
      setIsSendCustomerOpen(false);
      setValue(fieldNames.CONTACT, undefined);
      setSelectedContact(undefined);
    }
  }, [
    selectedContact,
    showEstimate,
    isEstimateEdit,
    isQuoteEdit,
    getValues,
    customerAccount,
    company,
    leadId,
    jobNumber,
    estimateTickedRows,
    quoteTickedRows,
    estimateSelectedField,
    quoteSelectedField,
    signatureState,
    buildDownloadPayload,
    downloadEstimate,
    handleQuoteSharedStatus,
    setSuccessModal,
    setIsSendCustomerOpen,
    setValue,
  ]);

  const handleSendToCustomerCancel = useCallback(() => {
    clearErrors();
    setStatusError(false);
    setIsSendCustomerOpen(false);
    setValue(fieldNames.COMPANY_NAME, undefined);
    setValue(fieldNames.PROJECT_NAME, undefined);
    setValue(fieldNames.CONTACT, undefined);
    setSelectedContact(undefined);
  }, [clearErrors, setIsSendCustomerOpen, setValue]);

  const handleAddContactModalSubmit = useCallback(async () => {
    let isValid = true;
    if (
      !getValues(fieldNames.CONTACT) ||
      getValues(fieldNames.CONTACT).trim() === ""
    ) {
      setError(fieldNames.CONTACT, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      isValid = false;
    } else if (!isValidName(getValues(fieldNames.CONTACT))) {
      setError(fieldNames.CONTACT, {
        type: "pattern",
        message: "Name should not contain numbers or special characters",
      });
      isValid = false;
    }

    if (
      !getValues(fieldNames.CONTACT_LAST_NAME) ||
      getValues(fieldNames.CONTACT_LAST_NAME).trim() === ""
    ) {
      setError(fieldNames.CONTACT_LAST_NAME, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      isValid = false;
    } else if (!isValidName(getValues(fieldNames.CONTACT_LAST_NAME))) {
      setError(fieldNames.CONTACT, {
        type: "pattern",
        message: "Name should not contain numbers or special characters",
      });
      isValid = false;
    }

    const emailValue = getValues(fieldNames.EMAIL);
    if (!emailValue || emailValue.trim() === "") {
      setError(fieldNames.EMAIL, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      isValid = false;
    } else if (!emailTestRegex.test(emailValue)) {
      setError(fieldNames.EMAIL, {
        type: "pattern",
        message: "Please enter a valid email",
      });
      isValid = false;
    }

    if (isValid) {
      const payload = {
        companyId: customerAccount,
        firstName: getValues(fieldNames.CONTACT),
        lastName: getValues(fieldNames.CONTACT_LAST_NAME),
        email: getValues(fieldNames.EMAIL),
      };

      const data = await addContact(payload, {
        onSuccess: async () => {
          await refetchContacts();
          setIsSuccessModalOpen(true);
          setIsAddCustomerOpen(false);
          setValue(fieldNames.CONTACT, "");
          setValue(fieldNames.EMAIL, "");
          setValue(fieldNames.CONTACT_LAST_NAME, "");
        },
        onError: (error) => message.error(error?.response?.data?.message),
      });
    }
  }, [
    getValues,
    setError,
    customerAccount,
    emailTestRegex,
    addContact,
    refetchContacts,
    setIsSuccessModalOpen,
    setIsAddCustomerOpen,
    setValue,
  ]);

  const handleAddContactModalClose = useCallback(async () => {
    clearErrors();
    setIsAddCustomerOpen(false);
    setIsSuccessModalOpen(false);
    setValue(fieldNames.CONTACT, "");
    setValue(fieldNames.EMAIL, "");
  }, [clearErrors, setIsAddCustomerOpen, setIsSuccessModalOpen, setValue]);

  return (
    <>
      <TNLoader isLoading={downloadLoading} />
      <div>
        <SuccessModal
          isWarningModalOpen={isSuccessModal}
          message={
            showEstimate === IS_QUOTE ? SUCCESS_CONTENT : SUCCESS_CONTENT_3
          }
          closeHandler={() => {
            setSuccessModal(false);
          }}
        />
        <SuccessModal
          isWarningModalOpen={isSuccessModalOpen}
          message={SUCCESS_CONTENT_2}
          closeHandler={() => setIsSuccessModalOpen(false)}
        />

        {/* Add Customer Contact Modal */}
        <Modal
          width={750}
          visible={isAddCustomerOpen}
          onCancel={handleAddContactModalClose}
          title="New Customer Contact"
          okText="Submit"
          confirmLoading={isContactLoading}
          onOk={handleAddContactModalSubmit}
          className={styles.modalWrapper}
          maskClosable={false}
          zIndex={10000}
        >
          <Row>
            <Col span={12}>
              <InputField
                name={fieldNames.CONTACT}
                options={[]}
                type={InputfieldTypes.TEXT}
                requiredField
                label="First Name"
                placeholder="Enter first name"
              />
            </Col>
            <Col span={12}>
              <InputField
                name={fieldNames.CONTACT_LAST_NAME}
                options={[]}
                type={InputfieldTypes.TEXT}
                requiredField
                label="Last Name"
                placeholder="Enter last name"
              />
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <InputField
                customSelect={false}
                name={fieldNames.EMAIL}
                type={InputfieldTypes.TEXT}
                requiredField
                label="Email"
                placeholder="Enter email"
              />
            </Col>
          </Row>
        </Modal>
        <Modal
          width={750}
          visible={isSendCustomerOpen}
          onCancel={handleSendToCustomerCancel}
          title="Send [Quote/Estimate] to Customer"
          okText="Submit"
          confirmLoading={downloadLoading}
          onOk={handleSendToCustomerModalSubmit}
          className={styles.modalWrapper}
          destroyOnClose
          maskClosable={false}
        >
          <div>
            <Row>
              <Col span={12}>
                <div className={styles.contactTypeContainer}>
                  <Label required label={"Contact"} name={fieldNames.CONTACT} />
                  <Select
                    name={fieldNames.CONTACT}
                    placeholder="Select Contact"
                    allowClear
                    bordered
                    className={styles.customerEmailSelect}
                    dropdownRender={(menu) => (
                      <div className={styles.CheckboxGroupContainer}>
                        <div
                          className={styles.ServiveLevelClearButtonContainer}
                        >
                          <TNButton
                            text="+ Add Contact"
                            type={
                              showEstimate === IS_ESTIMATE
                                ? ButtonType.PRIMARY
                                : ButtonType.DEFAULT
                            }
                            size={ButtonSize.LARGE}
                            onClick={handleAddCustomerModalOpen}
                            customStyles={{
                              width: "100%",
                              background: "#F1F5F9",
                              borderColor: "#F1F5F9",
                              textShadow: "none",
                              color: "grey",
                              display: "flex",
                              justifyContent: "start",
                              marginBottom: "0.5rem",
                            }}
                            shape={ButtonShape.DEFAULT}
                          />
                        </div>

                        {contactDropdownOptions.length > 0 ? (
                          menu
                        ) : (
                          <p className={styles.NoDataText}>
                            {"No Matching Search"}
                          </p>
                        )}
                      </div>
                    )}
                    status={isStatusError ? "error" : ""}
                    dropdownMatchSelectWidth={false}
                    value={selectedContact}
                    onChange={handleSelectContact}
                  >
                    {contactDropdownOptions?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {isStatusError ? (
                    <ErrorMessage message={ErrorTypes.DATE.REQUIRED} />
                  ) : (
                    <></>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </Modal>
        <div className={styles.TopButtonsContainer}>
          <div className={styles.LeftButtonsContainer}>
            <TNButton
              text="Generate Estimate"
              type={
                showEstimate === IS_ESTIMATE
                  ? ButtonType.PRIMARY
                  : ButtonType.DEFAULT
              }
              size={ButtonSize.LARGE}
              onClick={() => handleToggleShowEstimate(IS_ESTIMATE)}
              shape={ButtonShape.DEFAULT}
            />
            <TNButton
              text="Generate Quote"
              type={
                showEstimate === IS_QUOTE
                  ? ButtonType.PRIMARY
                  : ButtonType.DEFAULT
              }
              size={ButtonSize.LARGE}
              onClick={() => handleToggleShowEstimate(IS_QUOTE)}
              shape={ButtonShape.DEFAULT}
            />
          </div>

          <div className={styles.MiddleDropDownContainer}>
            <ConditionalRender
              visible={showEstimate === IS_QUOTE && !isQuoteEdit}
            >
              <Select
                bordered={false}
                key={JSON.stringify(quoteSelectedField)}
                type={InputfieldTypes.SELECT}
                name="templateQuoteDropDown"
                options={quoteTemplateRows}
                value={quoteSelectedField}
                dropdownMatchSelectWidth={false}
                onSelect={(val) => {
                  setQuoteFormContent(
                    quoteTemplateRows?.find((quote) => quote?.id === val)
                  );
                  setQuoteSelectedField(val);
                }}
              />
            </ConditionalRender>
            <ConditionalRender
              visible={showEstimate === IS_ESTIMATE && !isEstimateEdit}
            >
              <Select
                key={JSON.stringify(estimateSelectedField)}
                value={estimateSelectedField}
                bordered={false}
                type={InputfieldTypes.SELECT}
                name="templateEstimateDropDown"
                options={estimateTemplateRows}
                dropdownMatchSelectWidth={false}
                onSelect={(val) => {
                  setEstimateFormContent(
                    estimateTemplateRows?.find(
                      (estimate) => estimate?.id === val
                    )
                  );
                  setEstimateSelectedField(val);
                }}
              />
            </ConditionalRender>
          </div>

          <div className={styles.RightButtonsContainer}>
            <TNButton
              text={
                (showEstimate === IS_ESTIMATE && isEstimateEdit) ||
                (showEstimate === IS_QUOTE && isQuoteEdit)
                  ? "Save"
                  : "Edit"
              }
              type={
                (showEstimate === IS_ESTIMATE && isEstimateEdit) ||
                (showEstimate === IS_QUOTE && isQuoteEdit)
                  ? ButtonType.DEFAULT
                  : ButtonType.PRIMARY
              }
              disabled={showEstimate === IS_READ_ONLY}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              onClick={
                showEstimate === IS_QUOTE
                  ? handleToggleIsQuoteEdit
                  : showEstimate === IS_ESTIMATE
                  ? handleToggleIsEstimateEdit
                  : handleToggleIsEstimateEdit
              }
            />
            <TNButton
              text={
                showEstimate === IS_ESTIMATE
                  ? "Download Estimate"
                  : "Download Quote"
              }
              shape={ButtonShape.DEFAULT}
              customStyles={{
                backgroundColor: "#1890FF",
                borderColor: "#1890FF",
              }}
              disabled={
                showEstimate === IS_READ_ONLY || isQuoteEdit || isEstimateEdit
              }
              size={ButtonSize.LARGE}
              onClick={handleDownloadDocument}
            />
            <TNButton
              size={ButtonSize.LARGE}
              text={
                showEstimate === IS_ESTIMATE
                  ? "Send Estimate to Customer"
                  : "Send Quote to Customer"
              }
              disabled={
                showEstimate === IS_READ_ONLY || isQuoteEdit || isEstimateEdit
              }
              customStyles={{
                backgroundColor: "#0EC917",
                borderColor: "#0EC917",
              }}
              shape={ButtonShape.DEFAULT}
              onClick={handleSendToCustomerModalOpen}
            />
          </div>
        </div>
        <ConditionalRender visible={showEstimate === IS_ESTIMATE}>
          <BuildEstimate
            isEdit={isEstimateEdit}
            estimateTickedRows={estimateTickedRows}
            setEstimateTickedRows={setEstimateTickedRows}
            estimateFormContent={estimateFormContent}
            showEstimate={showEstimate}
            salesPerson={salesPerson}
          />
        </ConditionalRender>

        <ConditionalRender
          visible={showEstimate === IS_QUOTE || showEstimate === IS_READ_ONLY}
        >
          <Quote
            isEdit={isQuoteEdit}
            quoteTickedRows={quoteTickedRows}
            signatureState={signatureState}
            onSignatureUpdate={updateSignature}
            setQuoteTickedRows={setQuoteTickedRows}
            companyName={company}
            quoteFormContent={quoteFormContent}
            showEstimate={showEstimate}
            salesPerson={salesPerson}
            quoteDetails={QuoteDetails}
          />
        </ConditionalRender>
      </div>
    </>
  );
};

export default React.memo(BuildQuote);
